import { Dialog } from '@app/components'
import {
  type FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router'

/**
 * Unsaved-changes warnings for the long editors.
 *
 * Each editor reports whether it is dirty with `useUnsavedChanges(dirty)`. While anything is
 * dirty the browser asks before the tab closes or reloads (`beforeunload`), and the one
 * `UnsavedChangesGuard` (mounted in the admin shell) holds in-app navigation to another page
 * behind a "Stay / Leave" dialog. A router allows only one blocker, which is why editors register
 * here instead of calling `useBlocker` themselves. Dialog-based editors pass `dirty` (or
 * `trackChanges`) to `Dialog`, which asks before closing.
 */

export const UNSAVED_MESSAGE = 'You have unsaved changes. Leave without saving?'

interface Entry {
  dirty: boolean
  message: string
}
const entries = new Map<symbol, Entry>()

/** The message of the first editor with unsaved changes, or null. */
export function unsavedMessage(): string | null {
  for (const e of entries.values()) if (e.dirty) return e.message
  return null
}

/**
 * Forget every unsaved change (they re-register on the next edit). Call right before navigating
 * away after a successful save, when the editor hasn't re-rendered as clean yet.
 */
export function clearUnsavedChanges() {
  for (const e of entries.values()) e.dirty = false
}

export function useUnsavedChanges(dirty: boolean, opts: { message?: string } = {}) {
  const [id] = useState(() => Symbol('unsaved'))
  const message = opts.message ?? UNSAVED_MESSAGE
  // Registered during render so a navigation in the same tick sees the current value.
  entries.set(id, { dirty, message })
  useEffect(() => {
    entries.set(id, { dirty, message })
    return () => {
      entries.delete(id)
    }
  }, [id, dirty, message])
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!entries.get(id)?.dirty) return
      e.preventDefault()
      // Older browsers need returnValue set to show their own prompt.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [id, dirty])
  return useMemo(
    () => ({
      /** Mark this editor clean now (e.g. just before navigating after a save). */
      markClean: () => {
        const e = entries.get(id)
        if (e) e.dirty = false
      },
    }),
    [id],
  )
}

/** Compares two snapshots of a form's values (key order matters; build them the same way). */
export function differs(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) !== JSON.stringify(b)
}

/**
 * Dirty tracking for uncontrolled forms: any input or change inside the form since load or the
 * last `reset()` makes it dirty. Spread `bind` onto the <form>.
 */
export function useFormDirty() {
  const [dirty, setDirty] = useState(false)
  const mark = useCallback((e: FormEvent) => {
    // Search boxes and filters inside a form don't count.
    if ((e.target as HTMLElement).closest?.('[data-unsaved-ignore]')) return
    setDirty(true)
  }, [])
  const reset = useCallback(() => setDirty(false), [])
  return { dirty, reset, bind: { onInput: mark, onChange: mark } }
}

/** Stay / Leave when an in-app link would leave a page with unsaved changes. */
export function UnsavedChangesGuard() {
  const dataRouter = useContext(UNSAFE_DataRouterContext)
  // useBlocker needs a data router; plain <MemoryRouter> trees (tests) get beforeunload only.
  return dataRouter ? <NavigationBlocker /> : null
}

function NavigationBlocker() {
  const message = useRef(UNSAVED_MESSAGE)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Query-string changes stay on the page (tabs, filters, dialogs); only leaving it is held.
    if (currentLocation.pathname === nextLocation.pathname) return false
    const m = unsavedMessage()
    if (m) message.current = m
    return m !== null
  })
  const blocked = blocker.state === 'blocked'
  return (
    <Dialog open={blocked} onClose={() => blocker.reset?.()} title="Unsaved changes">
      <div className="stack--sm">
        <p className="mb-0">{message.current}</p>
        <div className="cluster">
          <button type="button" className="btn" data-autofocus onClick={() => blocker.reset?.()}>
            Stay
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => {
              clearUnsavedChanges()
              blocker.proceed?.()
            }}
          >
            Leave
          </button>
        </div>
      </div>
    </Dialog>
  )
}
